import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { toast } from "sonner";

function GoogleButton() {
  const goGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={goGoogle} data-testid="google-login-button" className="w-full flex items-center justify-center gap-3 border border-zinc-700 rounded py-3.5 text-sm font-semibold hover:bg-zinc-900 transition-colors">
      <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      Συνέχεια με Google
    </button>
  );
}

const inp = "w-full bg-[#0A0A0B] border border-zinc-700 rounded px-4 py-3.5 text-sm outline-none focus:border-[#FF2E4C] transition-colors";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Καλωσήρθες!");
      navigate(u.role === "admin" ? "/admin" : "/account");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <AuthShell title="ΣΥΝΔΕΣΗ" subtitle="Καλωσήρθες πίσω στο SKGWEAR">
      <form onSubmit={submit} className="space-y-3">
        <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} data-testid="login-email" required />
        <input type="password" placeholder="Κωδικός" value={password} onChange={(e) => setPassword(e.target.value)} className={inp} data-testid="login-password" required />
        <button disabled={loading} className="btn-red w-full py-3.5 text-sm font-bold tracking-widest uppercase rounded disabled:opacity-50" data-testid="login-submit">{loading ? "..." : "ΣΥΝΔΕΣΗ"}</button>
      </form>
      <Link to="/forgot-password" className="block text-center text-xs text-zinc-400 hover:text-[#FF2E4C] mt-3">Ξέχασες τον κωδικό;</Link>
      <Divider />
      <GoogleButton />
      <p className="text-center text-sm text-zinc-400 mt-6">Δεν έχεις λογαριασμό; <Link to="/register" className="text-[#FF2E4C] font-semibold">Εγγραφή</Link></p>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await register(form.email, form.password, form.name); toast.success("Ο λογαριασμός δημιουργήθηκε!"); navigate("/account"); }
    catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <AuthShell title="ΕΓΓΡΑΦΗ" subtitle="Γίνε μέλος της οικογένειας SKGWEAR">
      <form onSubmit={submit} className="space-y-3">
        <input placeholder="Ονοματεπώνυμο" value={form.name} onChange={set("name")} className={inp} data-testid="register-name" required />
        <input type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inp} data-testid="register-email" required />
        <input type="password" placeholder="Κωδικός" value={form.password} onChange={set("password")} className={inp} data-testid="register-password" required minLength={6} />
        <button disabled={loading} className="btn-red w-full py-3.5 text-sm font-bold tracking-widest uppercase rounded disabled:opacity-50" data-testid="register-submit">{loading ? "..." : "ΕΓΓΡΑΦΗ"}</button>
      </form>
      <Divider />
      <GoogleButton />
      <p className="text-center text-sm text-zinc-400 mt-6">Έχεις ήδη λογαριασμό; <Link to="/login" className="text-[#FF2E4C] font-semibold">Σύνδεση</Link></p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/auth/forgot-password", { email });
    } catch {}
    setSent(true);
  };
  return (
    <AuthShell title="ΕΠΑΝΑΦΟΡΑ" subtitle="Επαναφορά κωδικού πρόσβασης">
      {sent ? (
        <p className="text-center text-zinc-300 text-sm">Αν το email είναι εγγεγραμμένο, θα λάβεις σύνδεσμο επαναφοράς. Έλεγξε τα εισερχόμενά σου.</p>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} data-testid="forgot-email" required />
          <button className="btn-red w-full py-3.5 text-sm font-bold tracking-widest uppercase rounded" data-testid="forgot-submit">ΑΠΟΣΤΟΛΗ ΣΥΝΔΕΣΜΟΥ</button>
        </form>
      )}
      <Link to="/login" className="block text-center text-xs text-zinc-400 hover:text-[#FF2E4C] mt-4">← Επιστροφή στη σύνδεση</Link>
    </AuthShell>
  );
}

export function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const token = new URLSearchParams(window.location.search).get("token");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/auth/reset-password", { token, password });
      toast.success("Ο κωδικός άλλαξε! Συνδέσου.");
      navigate("/login");
    } catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <AuthShell title="ΝΕΟΣ ΚΩΔΙΚΟΣ" subtitle="Όρισε νέο κωδικό πρόσβασης">
      <form onSubmit={submit} className="space-y-3">
        <input type="password" placeholder="Νέος κωδικός" value={password} onChange={(e) => setPassword(e.target.value)} className={inp} data-testid="reset-password" required minLength={6} />
        <button className="btn-red w-full py-3.5 text-sm font-bold tracking-widest uppercase rounded" data-testid="reset-submit">ΑΛΛΑΓΗ ΚΩΔΙΚΟΥ</button>
      </form>
      <Link to="/login" className="block text-center text-xs text-zinc-400 hover:text-[#FF2E4C] mt-4">← Επιστροφή στη σύνδεση</Link>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="font-display text-3xl font-extrabold tracking-tight">SKG<span className="text-[#FF2E4C]">WEAR</span></Link>
          <h1 className="font-display text-2xl font-black uppercase tracking-tight mt-6">{title}</h1>
          <p className="text-zinc-500 text-sm mt-1">{subtitle}</p>
        </div>
        <div className="card-surface rounded-lg p-6 sm:p-8">{children}</div>
      </div>
    </div>
  );
}

function Divider() {
  return <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-zinc-800" /><span className="text-xs text-zinc-600 font-mono">Ή</span><div className="flex-1 h-px bg-zinc-800" /></div>;
}
